import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import NumberInput from '../../../../../../../../../../../../../../components/forms/number-input/NumberInput';
import SelectButtonInput from '../../../../../../../../../../../../../../components/forms/select-button-input/SelectButtonInput';
import { EMISSION_MAX_FRACTION_DIGIT } from '../../../../../../../../../../../../../../shared/constants/products.constant';
import { SprueTreatmentSelection } from '../../../../../../PcfWizard.type';
import WasteTreatmentDropDown from '../waste-treatment-dropdown/WasteTreatmentDropdown';
import { getSprueTreatmentTypeOptions } from './helper/getSprueTreatmentTypeOptions';

interface WasteManagementFormProps {
  processIndex: number;
}

const WasteManagementForm = ({ processIndex }: WasteManagementFormProps): JSX.Element => {
  const {
    control,
    trigger,
    formState: { submitCount },
  } = useFormContext();

  const { t } = useTranslation();

  const sprueTreatmentType = useWatch({
    name: `processes[${processIndex}].waste_management.sprue_treatment_type`,
    control,
  });

  const sprueWeight = useWatch({
    name: `processes[${processIndex}].waste_management.sprue_weight`,
    control,
  });

  useEffect(() => {
    if (submitCount > 0) {
      trigger([
        `processes[${processIndex}].waste_management.sprue_treatment_type`,
        `processes[${processIndex}].waste_management.sprue_waste_treatment`,
      ]);
    }
  }, [sprueWeight]);

  return (
    <div className='flex flex-column surface-ground border-round-2xl p-3 mt-2'>
      <h3 className='font-medium' style={{ fontSize: '16px' }}>
        {t(
          'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.title',
        )}
      </h3>
      <h4 className='mt-0 font-medium' style={{ fontSize: '14px' }}>
        {t(
          'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.sprueSubTitle',
        )}
      </h4>
      <div className='grid'>
        <div className='col-8'>
          <NumberInput
            name={`processes[${processIndex}].waste_management.sprue_weight`}
            control={control}
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.inputs.placeholders.sprue_weight',
            )}
            suffix=' kg'
            minFractionDigits={0}
            maxFractionDigits={EMISSION_MAX_FRACTION_DIGIT}
          />
        </div>
      </div>
      <div className='grid'>
        <div className='col-8 field'>
          <SelectButtonInput
            pt={{ root: { className: 'flex' }, button: { className: 'flex-grow-1 text-sm' } }}
            name={`processes[${processIndex}].waste_management.sprue_treatment_type`}
            control={control}
            options={getSprueTreatmentTypeOptions(t)}
          />
        </div>
      </div>

      {sprueTreatmentType === SprueTreatmentSelection.DISCARDED && (
        <div className='grid'>
          <div className='col-8 mb-2'>
            <WasteTreatmentDropDown
              name={`processes[${processIndex}].waste_management.sprue_waste_treatment`}
              label={t(
                'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.inputs.placeholders.sprueWasteTreatment',
              )}
              processIndex={processIndex}
            />
          </div>
        </div>
      )}
      <h4 className='font-medium mt-0' style={{ fontSize: '14px' }}>
        {t(
          'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.scrapSubTitle',
        )}
      </h4>
      <div className='grid'>
        <div className='col-8'>
          <WasteTreatmentDropDown
            name={`processes[${processIndex}].waste_management.scrap_waste_treatment`}
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.inputs.placeholders.scrapWasteTreatment',
            )}
            processIndex={processIndex}
          />
        </div>
        <div className='col-4'>
          <NumberInput
            name={`processes[${processIndex}].waste_management.scrap_rate`}
            control={control}
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.inputs.placeholders.scrapRate',
            )}
            suffix=' %'
            minFractionDigits={0}
            maxFractionDigits={2}
          />
        </div>
      </div>
    </div>
  );
};

export default WasteManagementForm;
